import clsx from 'clsx';
import { isToday, isWeekend } from 'date-fns';
import { Fragment, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ForecastSlice } from '../../../../../generated/graphql';
import { useProductionReduction } from './utils';

interface ProductionOverviewProps {
  deviceId: string;
  overviewMap: Map<string, (HTMLInputElement | null)[][]>;
  slices: readonly ForecastSlice[];
  editedSlices: Set<string>;
  days: readonly Date[];
  isFifteenMinutesResolution?: boolean;
  shouldUseLiveStats?: boolean;
}

export function ProductionOverview({
  deviceId,
  overviewMap,
  slices,
  editedSlices,
  days,
  isFifteenMinutesResolution,
  shouldUseLiveStats,
}: Readonly<ProductionOverviewProps>): ReactElement<ProductionOverviewProps> {
  const [t] = useTranslation();
  const { getSumFormatted, getAverageFormatted } = useProductionReduction(
    slices,
    days,
    isFifteenMinutesResolution,
    overviewMap.get(deviceId),
    {
      shouldUseLiveStats,
      slicesAreSortedByHour: true,
    },
  );

  return (
    <>
      <tr data-testid="prod-overview-sum-device-row">
        <th
          role="rowheader"
          className="min-w-40 box-border border-0 border-b border-solid border-divider bg-grey-50 px-6 py-2 text-start text-sm font-medium leading-none"
          data-testid="prod-overview-sum-device"
          colSpan={2}
        >
          {t('MARKET_LOCATION_FORECAST_TABLE_ENERGY_QUANTITY')}
        </th>
        {days.map((date, index) => (
          <Fragment key={`prod-sum-${deviceId}-${date.toISOString()}`}>
            <th
              className={clsx(
                'border-0 border-b border-l border-solid border-divider bg-grey-50 p-0 text-sm font-medium leading-none',
                {
                  'bg-blue-grey-200/10': isWeekend(date),
                  italic: slices.some((slice) => slice.date.getDate() === date.getDate() && editedSlices.has(slice.id)),
                },
              )}
            >
              <div className={clsx('px-6 py-2', { 'bg-green-50/40': isToday(date) })}>{getSumFormatted(date)}</div>
            </th>
            {index === days.length - 1 && (
              <th
                className={clsx('border-0 border-b border-solid border-divider bg-grey-50', {
                  'bg-blue-grey-200/5': isWeekend(date),
                })}
              />
            )}
          </Fragment>
        ))}
      </tr>
      <tr data-testid="prod-overview-avg-device-row">
        <th
          role="rowheader"
          className="min-w-40 box-border border-0 border-b border-solid border-divider bg-grey-50 px-6 py-2 text-start text-sm font-medium leading-none"
          data-testid="prod-overview-avg-device"
          colSpan={2}
        >
          {t('MARKET_LOCATION_FORECAST_TABLE_POWER_AVERAGE')}
        </th>
        {days.map((date, index) => (
          <Fragment key={`prod-avg-${deviceId}-${date.toISOString()}`}>
            <th
              className={clsx(
                'border-0 border-b border-l border-solid border-divider bg-grey-50 p-0 text-sm font-medium leading-none',
                {
                  'bg-blue-grey-200/10': isWeekend(date),
                  italic: slices.some((slice) => slice.date.getDate() === date.getDate() && editedSlices.has(slice.id)),
                },
              )}
            >
              <div className={clsx('px-6 py-2', { 'bg-green-50/40': isToday(date) })}>{getAverageFormatted(date)}</div>
            </th>
            {index === days.length - 1 && (
              <th
                className={clsx('border-0 border-b border-solid border-divider bg-grey-50', {
                  'bg-blue-grey-200/5': isWeekend(date),
                })}
              />
            )}
          </Fragment>
        ))}
      </tr>
    </>
  );
}
